import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { RESOURCE_NAME } from "common/authorization";
import { TASK_STATUS } from "common/constants";
import {
  getTypedObject,
  type TargetObject,
} from "common-client/utils/customObject";
import {
  GetAccountActivityDocument,
  GetActivitiesDocument,
  GetSubmissionDocument,
  GetTasksDocument,
  useCreateTaskMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import Modal from "../../Common/Modal";
import { Form } from "./Form";
import { LayoutContainer } from "./LayoutContainer";
import { TaskFormData, type TaskModalSubmission } from "./types";

type Props = {
  targetObject?: Maybe<TargetObject>;
  submission?: TaskModalSubmission;
  onCancel: () => void;
  onSuccess: () => void;
};

export function CreateTaskForm({
  targetObject,
  submission,
  onCancel,
  onSuccess,
}: Props) {
  const toasts = useStatusToasts();

  const form = useForm<TaskFormData>({
    values: {
      name: "",
      status: TASK_STATUS.TO_DO,
      assignees: { users: [] },
      targetSubmissionTypeId:
        submission?.submissionTypeVersion?.submissionType?.id,
      relatedSubmissionId: submission?.id,
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const { authorized } = useContext(AuthContext);

  const canCreateTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "create",
  });

  const [createTask, { loading }] = useCreateTaskMutation();

  const typedObject = getTypedObject(targetObject);
  const label = typedObject?.object.label;

  const onSubmit = async (formData: TaskFormData) => {
    if (!canCreateTask) {
      toasts.addWarningToast("You are not authorized to create tasks.");
      throw new Error("User is not authorized to create tasks");
    }

    if (!typedObject) {
      throw new Error("Target object not found");
    }

    const targetObjectPropertyPair = {
      objectId: typedObject.object.id,
      objectType: typedObject.type,
    };

    await createTask({
      variables: {
        data: {
          ...formData,
          ...targetObjectPropertyPair,
          assignees: {
            users: formData.assignees.users,
          },
        },
      },
      onCompleted: () => {
        toasts.addSuccessToast(`Your task at ${label} was created.`);
        track("Task created", { label, ...targetObjectPropertyPair });
        onSuccess();
      },
      onError: () => {
        toasts.addErrorToast(
          `Your task at ${label} was not created. Try again.`,
        );
      },
      refetchQueries: [
        GetTasksDocument,
        GetSubmissionDocument,
        GetActivitiesDocument,
        GetAccountActivityDocument,
      ],
    });
  };

  return (
    <LayoutContainer
      title="Add new task"
      subtitle={label}
      content={
        <FormProvider {...form}>
          <Form
            submitButtonLabel="Save"
            disabled={!canCreateTask || loading}
            onCancel={onCancel}
            onSubmit={onSubmit}
            targetObject={targetObject}
            disableAssociatedRecordTypeField={Boolean(submission)}
          />
        </FormProvider>
      }
      size="large"
    />
  );
}

type CreateTaskModalParams = Pick<Props, "targetObject" | "submission">;

export function useCreateTaskModal({
  targetObject,
  submission,
}: CreateTaskModalParams) {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={() => hideModal()}>
        <CreateTaskForm
          onCancel={hideModal}
          onSuccess={hideModal}
          targetObject={targetObject}
          submission={submission}
        />
      </Modal>
    ),
    [targetObject, submission],
  );

  return [showModal, hideModal] as const;
}
